import { useSelector } from 'react-redux';
import { useState } from 'react';
import { Loading } from '../../../components/loading/Loading';
import { formatDate } from '../../../common/formatDateText';
import { PaymentHistoryTimeline } from '../../../components/paymentHistory/PaymentHistoryTimeline';

export const TabPaymentHistory = () => {

    const { paymentsByUser, loading: loadingPaymentsByUser } = useSelector((state) => state.payment);
    const [expandedHistories, setExpandedHistories] = useState({});
    

    // Función para obtener el color según el estado
    const getStatusColor = (status) => {
        switch (status) {
            case 'validado':
                return {
                    bg: 'bg-emerald-50',
                    text: 'text-emerald-700',
                    border: 'border-emerald-200',
                    badge: 'bg-emerald-100 text-emerald-700'
                };
            case 'pendiente':
                return {
                    bg: 'bg-orange-50',
                    text: 'text-orange-700',
                    border: 'border-orange-200',
                    badge: 'bg-orange-100 text-orange-700'
                };
            case 'rechazado':
                return {
                    bg: 'bg-rose-50',
                    text: 'text-rose-700',
                    border: 'border-rose-200',
                    badge: 'bg-rose-100 text-rose-700'
                };
            default:
                return {
                    bg: 'bg-gray-50',
                    text: 'text-gray-700',
                    border: 'border-gray-200',
                    badge: 'bg-gray-100 text-gray-700'
                };
        }
    };

    const getPaymentType = (paymentType) => {
        switch (paymentType) {
            case 'pre registro':
                return {
                    bg: 'bg-purple-50',
                    text: 'text-purple-700',
                    border: 'border-purple-200',
                    badge: 'bg-purple-100 text-purple-700'
                };
            case 'suscripcion':
                return {
                    bg: 'bg-sky-50',
                    text: 'text-sky-700',
                    border: 'border-sky-200',
                    badge: 'bg-sky-100 text-sky-700'
                };
        }
    };

    // Función para formatear el monto
    const formatAmount = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount);
    };

    // Función para alternar el historial expandido
    const toggleHistory = (paymentId) => {
        setExpandedHistories(prev => {
            const newState = { ...prev };
            newState[paymentId] = !newState[paymentId];
            return newState;
        });
    };

    return (
        loadingPaymentsByUser ? (
            <Loading />
        ) : (
            <div className="h-full flex flex-col">
                {/* Título fijo */}
                <div className="flex-shrink-0 mb-6">
                    <div className="bg-white border border-gray-200 rounded-lg px-5 py-2 lg:py-4 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">
                                        Historial de Pagos
                                    </h3>
                                    <p className="text-xs text-gray-500 font-medium mt-0.5 hidden lg:block">
                                        Registro completo de transacciones
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
                                <span className="text-md text-gray-600 font-medium hidden lg:block">Total:</span>
                                <span className="text-md font-bold text-indigo-600">
                                    {paymentsByUser?.length || 0}
                                </span>
                                <span className="text-md text-gray-600 font-medium">pagos</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contenedor de tarjetas con scroll */}
                <div className="flex-1 overflow-auto">
                    {paymentsByUser && paymentsByUser.length > 0 ? (
                        <div className="space-y-4 pr-2">
                            {paymentsByUser.map((payment, index) => {
                                const statusColors = getStatusColor(payment.status);
                                const paymentTypeColors = getPaymentType(payment.paymentType);
                                const paymentId = payment._id || payment.id || payment.paymentFolio || index;
                                return (
                                    <div
                                        key={paymentId}
                                        className={`relative border-[1px] ${statusColors.border} rounded-lg overflow-hidden transition-all duration-300 hover:shadow-lg group `}
                                    >
                                        {/* Header del recibo */}
                                        <div className={`${statusColors.bg} p-4 border-b-2 ${statusColors.border}`}>
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <svg className={`w-5 h-5 ${statusColors.text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        <span className={`text-sm font-bold ${statusColors.text} tracking-wider`}>
                                                            {payment.paymentFolio}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-gray-600 font-bold capitalize">
                                                        {formatDate(payment.formatCreatedAt)}
                                                    </p>
                                                </div>
                                                <span className={`${statusColors.badge} px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide`}>
                                                    {payment.status}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Body del recibo */}
                                        <div className="bg-white p-4">
                                            <div className="space-y-3">
                                                {/* nombre del estudiante por (si existe) */}
                                                {payment.nameUser && (
                                                    <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                                                        <span className="text-sm text-gray-600 font-medium">Estudiante</span>
                                                        <span className="text-sm text-gray-900 font-semibold capitalize">
                                                            {payment.nameUser}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Monto */}
                                                {
                                                    payment.amount !== 0 && (
                                                        <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                                                            <span className="text-sm text-gray-600 font-medium">Monto</span>
                                                            <span className="text-2xl font-bold text-gray-900">
                                                                {formatAmount(payment.amount)}
                                                            </span>
                                                        </div>
                                                    )
                                                }
                                                
                                                {/* Tipo de pago */}
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm text-gray-600 font-medium">Tipo de pago</span>
                                                    <span className={`inline-flex items-center gap-1.5 ${paymentTypeColors.badge} px-3 py-1 rounded-lg text-sm font-semibold capitalize`}>
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                        </svg>
                                                        {payment.paymentType}
                                                    </span>
                                                </div>

                                                {/* Validado por (si existe) */}
                                                {payment.validatedBy && (
                                                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                                        <span className="text-sm text-gray-600 font-medium">Validado por</span>
                                                        <span className="text-sm text-gray-900 font-semibold capitalize">
                                                            {payment.validatedBy}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Fecha de actualización si es diferente */}
                                                {payment.formatUpdatedAt && (
                                                    <div className="flex items-center gap-2 pt-2">
                                                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <span className="text-xs text-gray-500 uppercase">
                                                            Actualizado: {formatDate(payment.formatUpdatedAt)}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Historial de Movimientos - Acordeón */}
                                            {payment.history && payment.history.length > 0 && (
                                                <div className="mt-4 pt-4 border-t border-gray-200">
                                                    <button
                                                        onClick={() => toggleHistory(paymentId)}
                                                        className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            <span className="text-sm font-bold text-gray-800">Historial de Movimientos</span>
                                                            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-indigo-600 bg-indigo-100 rounded-full">
                                                                {payment.history.length}
                                                            </span>
                                                        </div>
                                                        <svg
                                                            className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                                                                expandedHistories[paymentId] === true ? 'rotate-180' : ''
                                                            }`}
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </button>

                                                    {/* Contenido del acordeón */}
                                                    <div
                                                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                                            expandedHistories[paymentId] === true ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0'
                                                        }`}
                                                    >
                                                        <PaymentHistoryTimeline history={payment.history} />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Indicador lateral de estado */}
                                        <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${payment.status === 'validado' ? 'bg-emerald-500' :
                                                payment.status === 'pendiente' ? 'bg-amber-500' :
                                                    'bg-red-500'
                                            }`}></div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-center py-12">
                            <div className="bg-gray-100 rounded-full p-6 mb-4">
                                <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h4 className="text-lg font-semibold text-gray-700 mb-2">No hay pagos registrados</h4>
                            <p className="text-sm text-gray-500">Aún no se han realizado pagos para este usuario</p>
                        </div>
                    )}
                </div>
            </div>
        )
    )
}
